import { COMMENT_NODE, ELEMENT_NODE, TEXT_NODE, VElement, VNode } from "./common.ts"
import { children, done, first, insertBehind, next, NodeRange } from "./noderange.ts"

/* terminology: we mutate the _destination tree_ (`dst`)
 * to become like the _source tree_ (`src`)
 * preconditions: both trees must have elements as roots.
 * the destination tree must be connected to the document
 * or a shadow root.
 * both trees should have unique ids, both within themselves
 * and within the rest of the document/shadow root.
 * (the same id can occur within both trees, though.)
 * warning: this will remove the source node from the document
 * and mutate it in unspecified ways
 */

export const morph = (dst: NodeRange, src: VNode[]) => {
  let focused = document.activeElement

  let stash = Object.assign(
    document.createElement('div'),
    { hidden: true, className: 'murph-stash' }
  )
  // @ts-expect-error hacky way to avoid instanceof check
  ~(dst.root.body ?? dst.root).append(stash)

  morphNodes(stash, dst, src)

  // @ts-expect-error it was focused, so presumably it's focusable
  focused?.focus?.()
  stash.remove()
}

const morphNode = (stash: Element, dst: Node, src: VNode): Node => {
  if (dst.nodeType !== src.nodeType) {
    const real = realize(stash, src)
    dst.parentNode!.replaceChild(real, dst)
    return real
  } else if (src.nodeType === 1) {
    return morphElement(stash, dst as Element, src)
  } else if (src.nodeType === 3 || src.nodeType === 8) {
    if (dst.nodeValue !== src.nodeValue) dst.nodeValue = src.nodeValue
    return dst
  }
  throw "unreachable"
}

const morphElement = (stash: Element, dst: Element, src: VElement) => {
  morphNodes(stash, children(dst), src.childNodes)
  if (dst.tagName === src.tagName && dst.id === (src.attributes.id ?? "")) {
    morphAttributesAndProperties(dst, src)
    return dst
  } else {
    let real = realize(stash, src) as Element
    real.replaceChildren(...dst.children)
    dst.replaceWith(real)
    return real
  }
}

const morphAttributesAndProperties = (dst: Element, src: VElement) => {
  for (const [name, value] of Object.entries(src.attributes))
    if (dst.getAttribute(name) !== value) dst.setAttribute(name, value)
  for (const { name } of [...dst.attributes])
    if (!(name in src.attributes)) dst.removeAttribute(name)
  for (const prop in src.properties) {
    // event handler properties for custom events
    if (prop.startsWith('on') && !(prop in dst))
      // @ts-expect-error
      dst.addEventListener(prop.slice(2), (e: Event) => dst[prop]?.(e))
      // @ts-expect-error
    if (dst[prop] !== src.properties[prop]) dst[prop] = src.properties[prop]
  }
}

const morphNodes = (stash: Element, dst: NodeRange, src: VNode[]) => {
  let d = first(dst)
  for (let s of src) {
    let match: Node | null = null
    let id = (s as VElement).attributes?.id
    if (id) match = dst.root.getElementById(id)
    // [TODO] Possible to avoid making "find best dst node" O(n)?
    // This makes the overall match operation O(n^2).
    // Nanomorph seems not to worry about it.
    else for (let m = d; !done(dst, m) && !match; m = next(dst, m))
      if (decentMatch(m!, s)) match = m
    if (match) d = next(dst, morphNode(stash, insertBehind(dst, d, match), s))
    else d = next(dst, insertBehind(dst, d, realize(stash, s)))
  }
  while (!done(dst, d)) { let v = d!; d = next(dst, d); stash.append(v) }
}

const decentMatch = (a: Node, b: VNode) =>
  a.nodeType === b.nodeType && (
    b.nodeType !== 1 || (
      (a as Element).tagName === b.tagName &&
      (a as Element).id === ""
    )
  )

const realize = (stash: Element, src: VNode): Node => {
  if (src.nodeType === ELEMENT_NODE) {
    const el = document.createElement(src.tagName)
    morphAttributesAndProperties(el, src)
    morphNodes(stash, children(el), src.childNodes)
    src.inits.forEach(init => init(el))
    return el
  }
  if (src.nodeType === TEXT_NODE) return document.createTextNode(src.nodeValue!)
  if (src.nodeType === COMMENT_NODE) return document.createComment(src.nodeValue!)
  throw "unreachable"
}
